import { type ReactElement, type ReactNode, type Ref } from 'react';
import type { ShopperSearch } from '@/scapi';
import { cn } from '@/lib/utils';
import { usePlpFilters } from './hooks/use-plp-filters';
import { FilterBar } from './ui/organisms/filter-bar';
import { FilterSidebar } from './ui/organisms/filter-sidebar';
import { ProductResults } from './ui/organisms/product-results';
import type { LoadMoreProps } from './ui/molecules/load-more';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];
type ProductSearchResult = ShopperSearch.schemas['ProductSearchResult'];

export type ProductListingProps = {
    productGridDataKey?: string;
    result: ProductSearchResult;
    /** `enableInStockOnlySwitch` site preference — hides the "In stock only" switch when off. */
    inStockSwitchEnabled: boolean;
    isProductGridLoading: boolean;
    limit: number;
    handleProductClick?: (product: ProductSearchHit) => void;
    getProductUrl?: (product: ProductSearchHit) => string;
    children?: ReactNode;
    wrapperClassName?: string;
    appended?: ProductSearchHit[];
    firstNewIndex?: number | null;
    firstNewItemRef?: Ref<HTMLElement>;
    appendPending?: boolean;
    isRestoring?: boolean;
    restorationTarget?: number;
    loadMore?: LoadMoreProps;
    deferredProducts?: Promise<ProductSearchHit[]>;
    deferredSkeletonCount?: number;
};

export const ProductListing = ({
    productGridDataKey,
    result,
    inStockSwitchEnabled,
    isProductGridLoading,
    limit,
    handleProductClick,
    getProductUrl,
    children,
    wrapperClassName,
    appended,
    firstNewIndex,
    firstNewItemRef,
    appendPending,
    isRestoring = false,
    restorationTarget = 0,
    loadMore,
    deferredProducts,
    deferredSkeletonCount = 0,
}: ProductListingProps): ReactElement => {
    const filters = usePlpFilters(result);
    const uniqueProducts = (products: ProductSearchHit[]) => {
        const seen = new Set<string>();
        return products.filter((product) => {
            if (seen.has(product.productId)) return false;
            seen.add(product.productId);
            return true;
        });
    };
    const initialProducts = uniqueProducts(result.hits ?? []);
    const appendedProducts = uniqueProducts(appended ?? []);
    const products = uniqueProducts([...initialProducts, ...appendedProducts]);
    const skeletonCount = isRestoring ? restorationTarget : Math.min(limit, Math.max(0, result.total - result.offset));

    return (
        // Edge to edge: the page templates render this outside `section-container`.
        <div className={cn('flex flex-col', wrapperClassName)}>
            <FilterBar filters={filters} inStockSwitchEnabled={inStockSwitchEnabled} />

            <div className="flex flex-col lg:flex-row">
                <FilterSidebar
                    filters={filters}
                    inStockSwitchEnabled={inStockSwitchEnabled}
                    className="hidden lg:sticky lg:top-0 lg:flex lg:max-h-dvh lg:w-[360px] lg:shrink-0 lg:self-start lg:overflow-y-auto lg:border-t lg:border-snk-greige lg:pl-16 lg:pr-4"
                />

                <div className="min-w-0 flex-grow">
                    {children}
                    <ProductResults
                        key={productGridDataKey}
                        products={deferredSkeletonCount > 0 ? initialProducts : products}
                        appendedProducts={deferredSkeletonCount > 0 ? appendedProducts : undefined}
                        skeletonCount={skeletonCount}
                        isGridLoading={isProductGridLoading}
                        isRestoring={isRestoring}
                        isLoadingMore={appendPending}
                        loadMore={loadMore}
                        deferredProducts={deferredProducts}
                        deferredSkeletonCount={deferredSkeletonCount}
                        onProductClick={(product) => handleProductClick?.(product as ProductSearchHit)}
                        getProductUrl={
                            getProductUrl ? (product) => getProductUrl(product as ProductSearchHit) : undefined
                        }
                        firstNewIndex={firstNewIndex}
                        firstNewItemRef={firstNewItemRef}
                    />
                </div>
            </div>
        </div>
    );
};
